import type { SearchPerformancePagesTab } from '../api/searchPerformanceApi';
import type { PagesSort, PagesSortField } from './PagesSort';

/** The BFF's `tab` vocabulary; snake_case matches Mixpanel and the `tab` query param too. */
export type PagesTabKey = SearchPerformancePagesTab;

export type PagesTabDefinition = {
  key: PagesTabKey;
  label: string;
  /** Restored when the customer cancels their sort. Top is impressions desc; other tabs use crawl recency. */
  defaultSort: PagesSort;
  /**
   * A column can order a tab only when the view owning it lists the tab's
   * whole set: the PRERENDER view never lists pages it did not serve, the GSC
   * view never lists pages Google has no data for. Ordering from the wrong
   * view would silently shrink the tab.
   */
  sortableFields: readonly PagesSortField[];
  /** Header tooltip on the columns that cannot order this tab. */
  unsortableReason: string;
  emptyText: string;
};

const GSC_METRIC_FIELDS: readonly PagesSortField[] = ['impressions', 'clicks', 'ctrPct'];
const CRAWL_FIELDS: readonly PagesSortField[] = ['lastCrawledAt'];
const ALL_FIELDS: readonly PagesSortField[] = [...GSC_METRIC_FIELDS, ...CRAWL_FIELDS];

const PAGES_TAB_STRINGS = {
  crawlColumnsCannotOrder:
    'Sorting by Last crawled would leave out the pages Prerender did not serve in this period.',
  metricColumnsCannotOrder:
    'Sorting by a Search Console column would leave out the pages Google has no data for.',
};

export const PAGES_TABS: PagesTabDefinition[] = [
  {
    key: 'top',
    label: 'Top',
    defaultSort: { field: 'impressions', order: 'desc' },
    sortableFields: GSC_METRIC_FIELDS,
    unsortableReason: PAGES_TAB_STRINGS.crawlColumnsCannotOrder,
    emptyText: 'No pages found',
  },
  {
    key: 'with_impressions',
    label: 'With impressions',
    defaultSort: { field: 'lastCrawledAt', order: 'desc' },
    sortableFields: ALL_FIELDS,
    unsortableReason: '',
    emptyText: 'No pages had impressions in this period',
  },
  {
    key: 'no_impressions',
    label: 'No impressions',
    defaultSort: { field: 'lastCrawledAt', order: 'desc' },
    sortableFields: CRAWL_FIELDS,
    unsortableReason: PAGES_TAB_STRINGS.metricColumnsCannotOrder,
    emptyText: 'Every page Prerender served had at least one impression',
  },
  {
    key: 'delivery_errors',
    label: 'Delivery errors',
    defaultSort: { field: 'lastCrawledAt', order: 'desc' },
    sortableFields: CRAWL_FIELDS,
    unsortableReason: PAGES_TAB_STRINGS.metricColumnsCannotOrder,
    emptyText: 'No pages with errors',
  },
  {
    key: 'not_prerender_served',
    label: 'Not Prerender-served',
    defaultSort: { field: 'impressions', order: 'desc' },
    sortableFields: GSC_METRIC_FIELDS,
    unsortableReason: PAGES_TAB_STRINGS.crawlColumnsCannotOrder,
    // Load-bearing hedge — SearchPerformance/AGENTS.md: bucket-3 wording is constrained.
    emptyText: 'Every URL Google reports on also had a Prerender crawl observed in this period. Nothing to route.',
  },
];

/** Narrows a raw `tab` URL-param value; junk falls back to the first tab. */
export function isPagesTabKey(value: string | undefined): value is PagesTabKey {
  return PAGES_TABS.some((tab) => tab.key === value);
}

export function pagesTabDefinition(key: PagesTabKey): PagesTabDefinition {
  const tab = PAGES_TABS.find((candidate) => candidate.key === key);
  if (tab === undefined) {
    throw new Error(`Unknown pages tab: ${key}`);
  }
  return tab;
}
